import { Badge, useMantineTheme } from '@mantine/core'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { translate } from 'i18n/i18n'
import React, { useMemo } from 'react'
import { checkAuthorization } from 'utils/checkAuthorization'
import { fieldAssetStore } from '../../../../stores/fieldAssetStore/fieldAssetStore'
import { serviceCenterStore } from '../../store/serviceCenterStore'
import { getSuggestedLures } from '../../utils/getSuggestedLures'

type Props = {
  value?: number[]
  targetInsectId?: number | null
  propertyId: number
  disabled?: boolean
  onChange: (lureIds: number[]) => void
}

export const LuresMultiSelect: React.FC<Props> = ({
  value: defaultSelectedLureIds,
  targetInsectId = null,
  propertyId,
  disabled = false,
  onChange,
}) => {
  const theme = useMantineTheme()
  const luresKeyById = serviceCenterStore.useSelector(serviceCenterStore.selectors.getLures)
  const lures = serviceCenterStore.useSelector(serviceCenterStore.selectors.getLuresAsArray)
  const property = fieldAssetStore.getState().properties?.[propertyId]

  const lureOptions = useMemo(() => {
    const suggestedLures = getSuggestedLures(lures, targetInsectId, property)

    return [
      ...(suggestedLures.default || []).map(({ lureId, productName }) => ({
        value: lureId,
        label: productName,
        group: 'HISTORICAL',
      })),
      ...suggestedLures.others.map(({ lureId, productName }) => ({
        value: lureId,
        label: productName,
      })),
    ] as TOptions[]
  }, [lures, propertyId, targetInsectId])

  const lureViewable = checkAuthorization({ permission: 'VIEW_SSC_LURES', entity: 'FOR_ANY_ENTITY' })

  const selectedOptions = useMemo(() => {
    return (defaultSelectedLureIds ?? []).map((lureId) => ({
      value: lureId,
      label: luresKeyById[lureId].productName,
    }))
  }, [defaultSelectedLureIds, luresKeyById])

  const handleSelectionChange = (selectedOptions: TOptions[]) => {
    const selectedLureIds = selectedOptions.map((option) => option.value) as number[]

    onChange(selectedLureIds)
  }

  return (
    <MultiSelect
      data={lureOptions}
      onChange={handleSelectionChange}
      placeholder={translate.phrases.placeholder('Select lure type(s)')}
      selectedData={selectedOptions}
      disabled={disabled || !lureViewable}
      optionValueDecorator={(label, option) => (
        <Badge
          css={{
            color: theme.colors.midnight,
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'none',
          }}
        >
          {label} {option?.group === 'HISTORICAL' ? `(${translate.phrases.placeholder('Historical')})` : ''}
        </Badge>
      )}
    />
  )
}
